import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { usePageTitle } from "@/hooks/usePageTitle";
import WorkbenchLayout from "@/components/layout/workbench-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, FileText, Upload, Trash2, Edit, Folder, AlertCircle, Loader2, CheckCircle, Settings, Plus, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompositeFavicon } from "@/components/ui/composite-favicon";
import { formatDistanceToNow } from "date-fns";
import type { Station, File as FileType } from "@shared/schema";

export default function StationSettings() {
  const { id: stationId } = useParams<{ id: string }>();
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Check if coming from admin settings
  const urlParams = new URLSearchParams(window.location.search);
  const fromAdmin = urlParams.get('from') === 'admin';
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [citationTexts, setCitationTexts] = useState<{ [key: string]: string }>({});
  const [editWebsites, setEditWebsites] = useState<string[]>([]);

  // Fetch station details
  const { data: station, isLoading: stationLoading } = useQuery<Station>({
    queryKey: [`/api/stations/${stationId}`],
    enabled: !!stationId,
  });

  // Set page title dynamically based on station name
  usePageTitle(station ? `${station.name} Settings` : "Station Settings");

  // Fetch files
  const { data: files = [], isLoading: filesLoading, refetch: refetchFiles } = useQuery<FileType[]>({
    queryKey: [`/api/stations/${stationId}/files`],
    enabled: !!stationId,
    refetchInterval: 5000,
  });
  
  // Initialize conversation starters when station data loads

  // Delete station mutation
  const deleteStation = useMutation({
    mutationFn: async () => {
      await apiRequest(`/api/stations/${stationId}`, "DELETE");
    },
    onSuccess: () => {
      toast({
        title: "Station deleted",
        description: "The station has been permanently deleted.",
      });
      navigate("/");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete station",
        variant: "destructive",
      });
    },
  });

  // Update station mutation
  const updateStation = useMutation({
    mutationFn: async (data: { 
      name: string; 
      description: string; 
      websites?: string[]; 
      copyGeneratorPrompt?: string;
      copyGeneratorDisclaimer?: string;
    }) => {
      return await apiRequest(`/api/stations/${stationId}`, "PATCH", data);
    },
    onSuccess: () => {
      toast({
        title: "Station updated",
        description: "Your changes have been saved.",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/stations/${stationId}`] });
      setEditDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update station",
        variant: "destructive",
      });
    },
  });

  // Update feature toggles mutation
  const updateFeatureToggles = useMutation({
    mutationFn: async (data: { 
      copyEngineEnabled?: boolean; 
      copyEngineModel?: string;
      vbrEngineEnabled?: boolean; 
    }) => {
      return await apiRequest(`/api/stations/${stationId}`, "PATCH", data);
    },
    onSuccess: () => {
      toast({
        title: "Settings updated",
        description: "Feature settings have been saved.",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/stations/${stationId}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/stations'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update settings",
        variant: "destructive",
      });
    },
  });

  // Upload files mutation
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFiles) return;
      
      const formData = new FormData();
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        formData.append("files", file);
        // Add citation text for this file
        const citationText = citationTexts[file.name] || file.name;
        formData.append(`citationText_${i}`, citationText);
      }
      
      const response = await fetch(`/api/stations/${stationId}/files`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Upload failed");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Files uploaded",
        description: "Your files are being processed.",
      });
      setUploadDialogOpen(false);
      setSelectedFiles(null);
      setCitationTexts({});
      refetchFiles();
    },
    onError: (error: Error) => {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload files",
        variant: "destructive",
      });
    },
  });

  // Delete file mutation
  const deleteFile = useMutation({
    mutationFn: async (fileId: string) => {
      await apiRequest(`/api/files/${fileId}`, "DELETE");
    },
    onSuccess: () => {
      toast({
        title: "File deleted",
        description: "The file has been removed.",
      });
      refetchFiles();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete file",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = () => {
    if (selectedFiles && selectedFiles.length > 0) {
      uploadMutation.mutate();
    }
  };

  if (stationLoading || filesLoading) {
    return (
      <WorkbenchLayout>
        <div className="container mx-auto p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-96 w-full" />
        </div>
      </WorkbenchLayout>
    );
  }

  if (!station) {
    return (
      <WorkbenchLayout>
        <div className="container mx-auto p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Station not found.</AlertDescription>
          </Alert>
        </div>
      </WorkbenchLayout>
    );
  }

  return (
    <WorkbenchLayout>
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate(fromAdmin ? '/admin' : `/stations/${stationId}/vbr`)}
          className="mb-4"
          data-testid="button-back-to-station"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {fromAdmin ? 'Back to Admin Settings' : 'Back to VBR Engine'}
        </Button>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CompositeFavicon 
              faviconUrls={station.faviconUrls} 
              size="md"
              fallbackIcon={<Folder className="h-8 w-8 text-gray-500" />}
            />
            <div>
              <h1 className="text-3xl font-bold">{station.name} Settings</h1>
              <p className="text-gray-600 mt-1">
                {station.description || "No description provided"} 
                {station.createdAt && (
                  <> • Created {formatDistanceToNow(new Date(station.createdAt), { addSuffix: true })}</>
                )}
              </p>
            </div>
          </div>
          
          {/* Edit and Delete buttons moved to top right */}
          <div className="flex gap-2">
            <Dialog open={editDialogOpen} onOpenChange={(open) => {
              setEditDialogOpen(open);
              if (open) {
                setEditWebsites(station.websites || []);
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="button-edit-station">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Station</DialogTitle>
                  <DialogDescription>
                    Update your station's information
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="edit-name">Station Name</Label>
                    <Input
                      id="edit-name"
                      defaultValue={station.name}
                      placeholder="Enter station name"
                      data-testid="input-edit-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-description">Description</Label>
                    <Textarea
                      id="edit-description"
                      defaultValue={station.description || ""}
                      placeholder="Enter station description"
                      rows={3}
                      data-testid="input-edit-description"
                    />
                  </div>
                  <div>
                    <Label>Websites</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Add website URLs to automatically fetch favicons. For market stations with multiple radio stations, add each station's website.
                    </p>
                    <div className="space-y-2">
                      {editWebsites.map((website, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            type="url"
                            value={website}
                            onChange={(e) => {
                              const newWebsites = [...editWebsites];
                              newWebsites[index] = e.target.value;
                              setEditWebsites(newWebsites);
                            }}
                            placeholder="https://example.com"
                            data-testid={`input-edit-website-${index}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditWebsites(editWebsites.filter((_, i) => i !== index));
                            }}
                            data-testid={`button-remove-website-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditWebsites([...editWebsites, ''])}
                        className="w-full"
                        data-testid="button-add-website"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Website
                      </Button>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setEditDialogOpen(false)}
                    data-testid="button-cancel-edit"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      const nameInput = document.getElementById("edit-name") as HTMLInputElement;
                      const descInput = document.getElementById("edit-description") as HTMLTextAreaElement;
                      const validWebsites = editWebsites.filter(w => w.trim());
                      updateStation.mutate({
                        name: nameInput.value,
                        description: descInput.value,
                        websites: validWebsites.length > 0 ? validWebsites : undefined,
                      });
                    }}
                    disabled={updateStation.isPending}
                    data-testid="button-save-edit"
                  >
                    {updateStation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" data-testid="button-delete-station">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Station</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this station? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(false)}
                    data-testid="button-cancel-delete"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => deleteStation.mutate()}
                    disabled={deleteStation.isPending}
                    data-testid="button-confirm-delete"
                  >
                    {deleteStation.isPending ? "Deleting..." : "Delete Station"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Feature Settings - Controls which tools are available for this station */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <CardTitle>Feature Settings</CardTitle>
          </div>
          <CardDescription>
            Control which AI tools are available for this station
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="copy-engine-toggle" className="text-base font-medium">Copy Engine</Label>
                <p className="text-sm text-muted-foreground">
                  Generate FCC-compliant sponsorship messages
                </p>
              </div>
              <Switch
                id="copy-engine-toggle"
                data-testid="switch-copy-engine"
                checked={station?.copyEngineEnabled ?? true}
                onCheckedChange={(checked) => updateFeatureToggles.mutate({ copyEngineEnabled: checked })}
                disabled={updateFeatureToggles.isPending}
              />
            </div>
            
            {/* GPT Model Selector - only show when Copy Engine is enabled */}
            {(station?.copyEngineEnabled ?? true) && (
              <div className="flex items-center justify-between pl-4 border-l-2 border-blue-200 ml-2">
                <div className="space-y-0.5">
                  <Label htmlFor="copy-engine-model" className="text-base font-medium">GPT Model</Label>
                  <p className="text-sm text-muted-foreground">
                    Select which GPT model powers the Copy Engine
                  </p>
                </div>
                <Select
                  value={station?.copyEngineModel ?? "gpt-5"}
                  onValueChange={(value) => updateFeatureToggles.mutate({ copyEngineModel: value })}
                  disabled={updateFeatureToggles.isPending}
                >
                  <SelectTrigger className="w-[200px]" id="copy-engine-model" data-testid="select-copy-engine-model">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-5">GPT-5 Completions API</SelectItem>
                    <SelectItem value="gpt-5.2">GPT-5.2 Completions API</SelectItem>
                    <SelectItem value="gpt-5.2-responses">GPT-5.2 Responses API</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="vbr-engine-toggle" className="text-base font-medium">VBR Engine</Label>
                <p className="text-sm text-muted-foreground">
                  Create value-based recommendations for prospects
                </p>
              </div>
              <Switch
                id="vbr-engine-toggle"
                data-testid="switch-vbr-engine"
                checked={station?.vbrEngineEnabled ?? true}
                onCheckedChange={(checked) => updateFeatureToggles.mutate({ vbrEngineEnabled: checked })}
                disabled={updateFeatureToggles.isPending}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Knowledge Base - Full width since station card is removed */}
      <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>VBR Engine Knowledge Base</CardTitle>
                <CardDescription>
                  Upload and manage documents for AI analysis
                </CardDescription>
              </div>
              <div>
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="button-upload-files">
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Files
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Upload Files</DialogTitle>
                      <DialogDescription>
                        Select documents to add to your knowledge base. Supported formats: PDF, Word, Excel, PowerPoint, CSV, Text, Markdown
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Input
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md"
                          onChange={(e) => {
                            setSelectedFiles(e.target.files);
                            // Initialize citation texts for new files
                            if (e.target.files) {
                              const newCitationTexts: { [key: string]: string } = {};
                              for (let i = 0; i < e.target.files.length; i++) {
                                const file = e.target.files[i];
                                newCitationTexts[file.name] = citationTexts[file.name] || "";
                              }
                              setCitationTexts(newCitationTexts);
                            }
                          }}
                          data-testid="input-file-upload"
                        />
                      </div>
                      {selectedFiles && selectedFiles.length > 0 && (
                        <div className="space-y-3">
                          <p className="text-sm font-medium">Citation Text for Each File:</p>
                          {Array.from(selectedFiles).map((file, index) => (
                            <div key={index} className="space-y-2 p-3 border rounded-lg">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium">{file.name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {Math.round(file.size / 1024)} KB
                                </Badge>
                              </div>
                              <Input
                                placeholder="e.g., Company Annual Report 2024"
                                value={citationTexts[file.name] || ""}
                                onChange={(e) => {
                                  setCitationTexts({
                                    ...citationTexts,
                                    [file.name]: e.target.value
                                  });
                                }}
                                className="text-sm"
                                data-testid={`input-citation-${index}`}
                              />
                              <p className="text-xs text-gray-500">
                                This text will be used when citing this document in VBRs
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-end space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setUploadDialogOpen(false);
                            setSelectedFiles(null);
                          }}
                          data-testid="button-cancel-upload"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleFileUpload}
                          disabled={!selectedFiles || selectedFiles.length === 0 || uploadMutation.isPending}
                          data-testid="button-confirm-upload"
                        >
                          {uploadMutation.isPending ? "Uploading..." : "Upload Files"}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {files.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No files uploaded yet</p>
                <p className="text-sm text-gray-400">Upload files to start building your knowledge base</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {files.map((file: any) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                    data-testid={`file-item-${file.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium" data-testid={`text-filename-${file.id}`}>
                            {file.originalName}
                          </p>
                          {file.metadata?.sourceType === 'generic' && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">Generic Data</Badge>
                          )}
                          {file.metadata?.sourceType === 'tapscan' && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Tapscan</Badge>
                          )}
                          {file.metadata?.sourceType === 'profile' && (
                            <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">NPR Profile</Badge>
                          )}
                          {file.metadata?.processingStatus === 'processing' && (
                            <div className="flex items-center gap-1 text-yellow-600">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-xs">Processing embeddings...</span>
                            </div>
                          )}
                          {file.metadata?.processingStatus === 'completed' && (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-xs">Ready</span>
                            </div>
                          )}
                          {file.metadata?.processingStatus === 'failed' && (
                            <div className="flex items-center gap-1 text-red-600">
                              <AlertCircle className="h-4 w-4" />
                              <span className="text-xs">Processing failed</span>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {file.fileSize < 1024 * 1024 
                            ? `${Math.round(file.fileSize / 1024)} KB`
                            : `${(file.fileSize / (1024 * 1024)).toFixed(1)} MB`} • 
                          Facts: {(file.metadata?.factCount || 0).toLocaleString()} • 
                          {formatDistanceToNow(new Date(file.createdAt), { addSuffix: true })}
                        </p>
                        {file.citationText && (
                          <p className="text-xs text-gray-600 italic mt-1">
                            Citation: {file.citationText}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteFile.mutate(file.id)}
                      disabled={deleteFile.isPending}
                      data-testid={`button-delete-file-${file.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
      </Card>

      {/* Copy Generator Prompt - Below Knowledge Base */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Copy Generator Prompt</CardTitle>
          <CardDescription>
            Customize the AI prompt for generating FCC-compliant sponsorship messages for this station
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            id="copy-prompt"
            defaultValue={station.copyGeneratorPrompt || ""}
            placeholder="Enter a custom prompt that will be injected into the Copy Engine for this station..."
            rows={4}
            data-testid="input-copy-prompt"
          />
          <p className="text-xs text-gray-500 mt-2">
            This prompt will be used to customize AI responses for sponsorship messages in the Copy Engine
          </p>
          <p className="text-xs text-gray-500 mt-2">
            <strong>Available Variables:</strong> Use these placeholders in your prompt (they'll be replaced with form data):
          </p>
          <ul className="text-xs text-gray-500 mt-1 ml-4 list-disc">
            <li>{'{sponsorName}'} - Sponsor's name</li>
            <li>{'{copyType}'} - Type of copy (broadcast or event)</li>
            <li>{'{targetType}'} - Outputs "company" for broadcast or "event" for event copy</li>
            <li>{'{sponsorWebsite}'} - Sponsor's website</li>
            <li>{'{previousCopy}'} - Previously used copy</li>
            <li>{'{moreInfoLink}'} - Link for more info</li>
            <li>{'{eventName}'} - Name of the event</li>
            <li>{'{eventWebsite}'} - Event website</li>
            <li>{'{eventDatesAndTimes}'} - Event dates and times</li>
            <li>{'{eventLocation}'} - Event location</li>
            <li>{'{ticketsLink}'} - Tickets/info link</li>
            <li>{'{additionalDetails}'} - Additional details provided</li>
            <li>{'{webContext}'} - AI-extracted key information from all provided URLs (auto-generated)</li>
          </ul>
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => {
                const copyPromptInput = document.getElementById("copy-prompt") as HTMLTextAreaElement;
                updateStation.mutate({
                  name: station.name,
                  description: station.description || "",
                  copyGeneratorPrompt: copyPromptInput.value || undefined,
                });
              }}
              disabled={updateStation.isPending}
              data-testid="button-save-copy-prompt"
            >
              {updateStation.isPending ? "Saving..." : "Save Prompt"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Copy Generator Disclaimer - Below Copy Generator Prompt */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Copy Generator Disclaimer</CardTitle>
          <CardDescription>
            Customize the disclaimer text displayed in the Copy Engine for this station
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            id="copy-disclaimer"
            defaultValue={station.copyGeneratorDisclaimer || ""}
            placeholder="Enter the disclaimer text that appears below the Copy Engine header (e.g., 'Generate FCC-compliant sponsorship messages for on-air use')"
            rows={2}
            data-testid="input-copy-disclaimer"
          />
          <p className="text-xs text-gray-500 mt-2">
            This text will be displayed below the Copy Engine header on the station's copy page
          </p>
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => {
                const copyDisclaimerInput = document.getElementById("copy-disclaimer") as HTMLTextAreaElement;
                updateStation.mutate({
                  name: station.name,
                  description: station.description || "",
                  copyGeneratorDisclaimer: copyDisclaimerInput.value || undefined,
                });
              }}
              disabled={updateStation.isPending}
              data-testid="button-save-copy-disclaimer"
            >
              {updateStation.isPending ? "Saving..." : "Save Disclaimer"}
            </Button>
          </div>
        </CardContent>
      </Card>

    </WorkbenchLayout>
  );
}